import { Stack } from "@mui/material";
import type {
  GridColDef,
  GridColumnGroup,
  GridEventListener,
  GridPinnedRowsProp,
  GridRenderCellParams,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid.tsx";
import { isPinnedRowNode } from "@entrilia/shared/components/grid/gridHelper.ts";
import ReportLoadingOverlay from "../../common/ReportLoadingOverlay.tsx";
import useMergedSx from "@entrilia/shared/hooks/useMergedSx.ts";
import { isEmptyObject } from "@entrilia/shared/utilities/objectHelper.ts";
import type {
  PivotDataGrandTotals,
  PivotRowResponse,
  PivotRowValueCell,
  ReportField,
} from "../../../../../../../api/types/svTypes.ts";
import useAutosizeColumns from "../../../hooks/useAutosizeColumns.ts";
import useGridColumnWidths from "../../../hooks/useGridColumnWidths.ts";
import { getBiDataGridStyles } from "../../../utilities/Utilities.tsx";
import type { DataGridCustomProps } from "../../common/DataGridCustomProps.tsx";
import PivotExpansionButton from "./PivotExpansionButton.tsx";
import {
  buildPivotRowHierarchy,
  getPivotLeafRows,
  getPivotRowDimension,
  getPivotGroupExpansionScope,
  togglePivotGroupLevel,
  getVisiblePivotRows,
  type PivotRowHierarchy,
} from "./utils/pivotRowHierarchy.ts";
import {
  createGrandTotalLinesCell,
  createGrandTotalMeasureCell,
  createLinesCell,
  createMeasureCell,
  createTabularFormLinesCell,
  NON_PIVOT_MEASURE_PLACEHOLDER_CLASS,
  TABULAR_FORM_LINES_PLACEHOLDER_CLASS,
} from "./renderDisplayValue.tsx";

interface Props extends DataGridCustomProps {
  rows: PivotRowResponse[];
  columns: GridColDef[];
  columnGroups: GridColumnGroup[];
  loading: boolean;
  rowFields: ReportField[];
  grandTotals?: PivotDataGrandTotals;
  showSummaries?: boolean;
  hideGroupRows?: boolean;
  onValueCellClick: (cell: PivotRowValueCell | undefined) => void;
  onSortModelChange: (model: GridSortModel) => void;
  sortModel: GridSortModel;
}

const pivotGridSx = {
  ".MuiDataGrid-columnHeader": {
    [`&.MuiDataGrid-columnHeader--filledGroup.${NON_PIVOT_MEASURE_PLACEHOLDER_CLASS}`]: {
      borderBottom: "none",
    },
  },
  [`[role="row"]:not([aria-rowindex="1"]) .MuiDataGrid-columnHeader.${TABULAR_FORM_LINES_PLACEHOLDER_CLASS}`]: {
    borderTop: "1px solid var(--DataGrid-rowBorderColor)",
  },
};

interface GridColumnsOptions {
  columns: GridColDef[];
  columnWidths: ReadonlyMap<string, number>;
  rowFields: ReportField[];
  showSummaries?: boolean;
  disableCellClick?: boolean;
  hideGroupRows?: boolean;
  rowHierarchy: PivotRowHierarchy;
  collapsedRows: ReadonlySet<string>;
  onToggleRow: (key: string) => void;
  onToggleAllRows: (level?: number) => void;
}

const getGridColumns = (options: GridColumnsOptions): GridColDef[] =>
  options.columns.map((column) => {
    const isLines = column.field === "lines";
    const level = column.field.startsWith("lines_") ? Number(column.field.slice("lines_".length)) : undefined;
    if (isLines || level !== undefined) {
      const scope = getPivotGroupExpansionScope(options.rowHierarchy, options.collapsedRows, level);
      const label = isLines
        ? `${scope.expanded ? "Collapse" : "Expand"} all rows`
        : `${scope.expanded ? "Collapse" : "Expand"} all ${column.description ?? column.headerName ?? "row"} groups and subgroups`;
      return {
        ...column,
        width: options.columnWidths.get(column.field) ?? column.width,
        renderHeader: (params) => (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            {scope.hasGroupsAtLevel && !options.hideGroupRows && (
              <PivotExpansionButton
                all
                expanded={scope.expanded}
                onToggle={() => options.onToggleAllRows(level)}
                label={label}
              />
            )}
            {column.renderHeader?.(params)}
          </Stack>
        ),
        renderCell: (params: GridRenderCellParams<PivotRowResponse>) => {
          if (options.showSummaries && isPinnedRowNode(params.rowNode)) {
            return isLines || level === 0 ? createGrandTotalLinesCell() : null;
          }
          const cell = getPivotRowDimension(params.row);
          const node = cell && options.rowHierarchy.nodes.get(cell.rowId);
          const expansion =
            node?.hasChildren && (isLines || cell?.level === level)
              ? { expanded: !options.collapsedRows.has(node.key), onToggle: () => options.onToggleRow(node.key) }
              : undefined;
          if (isLines) {
            return createLinesCell(cell, options.rowFields, expansion);
          }
          const levelCell = params.row[column.field];
          return createTabularFormLinesCell(
            levelCell && "dimensionName" in levelCell ? levelCell : undefined,
            options.rowFields,
            level ?? 0,
            cell?.level === level,
            !options.hideGroupRows,
            expansion
          );
        },
      };
    }
    return {
      ...column,
      width: options.columnWidths.get(column.field) ?? column.width,
      renderCell: (params: GridRenderCellParams<PivotRowResponse, PivotRowValueCell>) =>
        options.showSummaries && isPinnedRowNode(params.rowNode)
          ? createGrandTotalMeasureCell(params.value, !!options.disableCellClick)
          : createMeasureCell(params.value, options.rowFields, !!options.disableCellClick),
    };
  });

const PivotGrid = ({
  columnGroups,
  columns,
  loading,
  rowFields,
  rows,
  size,
  onValueCellClick,
  grandTotals,
  showSummaries,
  hideGroupRows,
  disableCellClick,
  onSortModelChange,
  sortModel,
}: Props) => {
  const apiRef = useGridApiRef();

  const [collapsedRows, setCollapsedRows] = useState<ReadonlySet<string>>(() => new Set());
  const { columnWidths, handleColumnWidthChange } = useGridColumnWidths();
  const manuallyResizedColumnsRef = useRef(new Set<string>());
  const handleColumnResize = useCallback<GridEventListener<"columnResize">>(({ colDef }) => {
    manuallyResizedColumnsRef.current.add(colDef.field);
  }, []);
  const rowHierarchy = useMemo(() => buildPivotRowHierarchy(rows), [rows]);
  const visibleRows = useMemo(
    () =>
      hideGroupRows ? getPivotLeafRows(rows, rowHierarchy) : getVisiblePivotRows(rows, rowHierarchy, collapsedRows),
    [rows, rowHierarchy, collapsedRows, hideGroupRows]
  );
  const handleToggleRow = useCallback((key: string) => {
    setCollapsedRows((previous) => {
      const next = new Set(previous);
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleToggleAllRows = useCallback(
    (level?: number) => {
      setCollapsedRows((previous) => togglePivotGroupLevel(rowHierarchy, previous, level));
    },
    [rowHierarchy]
  );

  // MUI DataGrid can keep focus on a removed column while column definitions are rebuilding.
  // Clearing focus avoids crashes in internal getCellParams/getRowValue handlers.
  useLayoutEffect(() => {
    apiRef.current?.setState((gridState) => ({
      ...gridState,
      focus: { cell: null, columnHeader: null, columnHeaderFilter: null, columnGroupHeader: null },
    }));
  }, [columns, apiRef]);

  const { autosizeOptions } = useAutosizeColumns(apiRef, [columns, visibleRows], manuallyResizedColumnsRef);

  const gridColumns = useMemo(
    () =>
      getGridColumns({
        columns,
        columnWidths,
        rowFields,
        showSummaries,
        disableCellClick,
        hideGroupRows,
        rowHierarchy,
        collapsedRows,
        onToggleRow: handleToggleRow,
        onToggleAllRows: handleToggleAllRows,
      }),
    [
      columns,
      columnWidths,
      rowFields,
      showSummaries,
      disableCellClick,
      hideGroupRows,
      rowHierarchy,
      collapsedRows,
      handleToggleRow,
      handleToggleAllRows,
    ]
  );

  const pinnedLeftFields = useMemo(
    () => columns.filter((c) => c.field === "lines" || c.field.startsWith("lines_")).map((c) => c.field),
    [columns]
  );

  const pinnedRows = useMemo((): GridPinnedRowsProp => {
    if (!grandTotals || isEmptyObject(grandTotals)) {
      return {};
    }
    return { bottom: [grandTotals] };
  }, [grandTotals]);

  const sx = useMergedSx(getBiDataGridStyles, { maxWidth: size.width }, pivotGridSx);

  return (
    <DataGrid<PivotRowResponse | PivotDataGrandTotals>
      apiRef={apiRef}
      columns={gridColumns}
      onColumnWidthChange={handleColumnWidthChange}
      onColumnResize={handleColumnResize}
      columnGroupingModel={columnGroups}
      rows={visibleRows}
      getRowId={getRowId}
      pinnedRows={pinnedRows}
      sx={sx}
      containerSx={{ alignItems: "center" }}
      scrollContainerSx={{ width: size.width }}
      rowHeight={32}
      pinnedColumns={{ left: pinnedLeftFields }}
      autosizeOptions={autosizeOptions}
      loading={loading}
      disableColumnFilter={false}
      disableColumnSelector={false}
      disableColumnMenu={false}
      showCellVerticalBorder
      showColumnVerticalBorder
      columnHeaderHeight={32}
      slots={gridSlots}
      onCellClick={({ field, row }) => {
        if (field === "lines" || field.startsWith("lines_")) {
          return;
        }
        const value = row[field];
        onValueCellClick(value && "id" in value ? value : undefined);
      }}
      onSortModelChange={onSortModelChange}
      sortModel={sortModel}
      sortingMode="server"
      disableMultipleColumnsSorting
      sortingOrder={["asc", "desc"]}
    />
  );
};

const getRowId = (row: PivotRowResponse): number | string => getPivotRowDimension(row)?.rowId ?? "pivot-grand-total";

const gridSlots = { loadingOverlay: ReportLoadingOverlay };

export default PivotGrid;
